import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AddressAPIService } from '@vendasta/address';
import { SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  publishReplay,
  refCount,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { PartnerService } from '../core/partner.service';
import { CompanyProfileService } from './company-profile.service';
import { PartnerMailingAddressResponse } from './partner';

@Component({
  templateUrl: 'company-profile.component.html',
  styleUrls: ['company-profile.component.scss'],
  standalone: false,
})
export class CompanyProfileComponent implements OnInit, OnDestroy {
  updateCompanyFormGroup: UntypedFormGroup;
  partnerId: string;
  hasMailingInformation = false;
  isUpdatingProfile = false;
  loading = true;
  private subscriptions: Subscription[] = [];
  partnerMailingAddressInfo$: Observable<PartnerMailingAddressResponse>;
  partnerName: string;

  countryOptions$: Observable<SelectInputOptionInterface[]>;
  stateProvOptions$$ = new BehaviorSubject<SelectInputOptionInterface[]>([]);
  private readonly onDestroy = new Subject<void>();

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private companyProfileService: CompanyProfileService,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private partnerService: PartnerService,
    private readonly configService: AppConfigService,
    private readonly addressService: AddressAPIService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.partnerId$
        .pipe(
          switchMap((pid) => {
            this.partnerId = pid;
            return this.partnerService.getPartnerByPartnerId(pid);
          }),
        )
        .subscribe((partner) => {
          this.partnerName = partner.name;
          this.companyProfileService.loadCompanyProfileData(this.partnerId);
        }),
    );

    this.partnerMailingAddressInfo$ = this.companyProfileService.partnerMailingInformation$.pipe(
      tap((partnerMailingAddress) => {
        this.setUpForm(partnerMailingAddress);
      }),
    );

    this.countryOptions$ = this.addressService.listAllCountryOptions().pipe(
      take(1),
      map((countries) => {
        if (!countries) {
          return [];
        }
        return countries.sort((a, b) => {
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
      }),
      map((countries) =>
        countries.map((country) => ({
          value: country?.code || '',
          label: country?.name || '',
        })),
      ),
      publishReplay(1),
      refCount(),
    );
  }

  private updateStateProvWithCountry(state: string): void {
    this.updateCompanyFormGroup.controls.country.valueChanges
      .pipe(
        takeUntil(this.onDestroy),
        filter((country) => !!country),
        distinctUntilChanged(),
        tap((country) => this.setCountry(country, state)),
      )
      .subscribe();
  }

  private setCountry(country: string, state: string): void {
    this.addressService
      .getCountryConfiguration(country, undefined)
      .pipe(
        map((countryConfig) =>
          countryConfig?.zones?.map((zone) => ({
            value: zone?.code || '',
            label: zone?.name || '',
          })),
        ),
        catchError(() => []),
        tap((stateProvs: SelectInputOptionInterface[]) => this.stateProvOptions$$.next(stateProvs)),
        tap((stateProvs: SelectInputOptionInterface[]) => {
          if (!!stateProvs && stateProvs.findIndex((sp) => sp.value === state) > 0) {
            this.updateCompanyFormGroup.setControl('stateProv', new UntypedFormControl(state));
          } else {
            this.updateCompanyFormGroup.setControl('stateProv', new UntypedFormControl(null));
          }
        }),
        take(1),
      )
      .subscribe();
  }

  updateCompanyProfile(): void {
    if (this.updateCompanyFormGroup.invalid) {
      this.snackbarService.errorSnack(this.translate.instant('COMPANY_PROFILE.INVALID_FORM'));
      return;
    }

    if (this.updateCompanyFormGroup.valid) {
      this.partnerName = this.updateCompanyFormGroup.controls.company_name.value;

      this.isUpdatingProfile = true;

      const companyProfileFormValues = {
        partnerId: this.partnerId,
        mailingCompanyName: this.updateCompanyFormGroup.controls.company_name.value,
        mailingAddress: this.updateCompanyFormGroup.controls.address.value,
        mailingAddress2: this.updateCompanyFormGroup.controls.additionalAddressLine.value,
        mailingCity: this.updateCompanyFormGroup.controls.city.value,
        mailingState: this.updateCompanyFormGroup.controls.stateProv.value || '',
        mailingCountry: this.updateCompanyFormGroup.controls.country.value,
        mailingPostalCode: this.updateCompanyFormGroup.controls.postalCode.value,
        mailingPhoneNumber: this.updateCompanyFormGroup.controls.phone_number.value,
        mailingWebsiteAddress: this.updateCompanyFormGroup.controls.website_address.value,
        mailingEmailAddress: this.updateCompanyFormGroup.controls.email_address.value,
      };
      this.companyProfileService
        .updatePartnerMailingAddress(companyProfileFormValues)
        .pipe(take(1))
        .subscribe((success) => {
          if (success) {
            window.setTimeout(() => {
              this.companyProfileService.forceLoadCompanyProfileData(this.partnerId);
              this.configService.load(this.partnerId);
              this.isUpdatingProfile = false;
              this.snackbarService.successSnack(this.translate.instant('COMPANY_PROFILE.UPDATE_SUCCESS'));
            }, 3000);
          } else {
            this.snackbarService.errorSnack(this.translate.instant('COMPANY_PROFILE.UPDATE_ERROR'));
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.onDestroy.complete();
  }

  setUpForm(partnerMailingAddress: PartnerMailingAddressResponse): void {
    this.updateCompanyFormGroup = new UntypedFormGroup({
      company_name: new UntypedFormControl(
        this.partnerName || this.configService.config.partnerName || '',
        Validators.required,
      ),
      phone_number: new UntypedFormControl(partnerMailingAddress?.mailing_phone_number || ''),
      website_address: new UntypedFormControl(partnerMailingAddress?.mailing_website_address || ''),
      email_address: new UntypedFormControl(partnerMailingAddress?.mailing_email_address || ''),
      country: new UntypedFormControl(partnerMailingAddress?.mailing_country || ''),
      address: new UntypedFormControl(partnerMailingAddress?.mailing_address || ''),
      additionalAddressLine: new UntypedFormControl(partnerMailingAddress?.mailing_address_2 || ''),
      city: new UntypedFormControl(partnerMailingAddress?.mailing_city || ''),
      postalCode: new UntypedFormControl(partnerMailingAddress?.mailing_postal_code || ''),
    });
    this.updateStateProvWithCountry(partnerMailingAddress?.mailing_state || '');
  }
}
